// The composer's photo strip, and the state behind it.
//
// Two things make this more involved than a file input. First, the bytes are encrypted, so a photo
// cannot be an <img src> pointing at the API — each one is fetched, opened with the DEK, and turned
// into an object URL that has to be revoked again or the decrypted image leaks for the life of the
// tab. Second, the Worker refuses photos for an entry that does not exist yet, and a new entry has no
// row until it is saved: so pictures chosen while writing are held locally and uploaded the moment
// the entry lands, which is the same trick iOS plays with its `sessionEntryID`.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  deleteEntryPhoto,
  fetchEntryPhotos,
  loadEntryPhoto,
  updateEntryPhotoCrop,
  uploadEntryPhoto,
  DEFAULT_CROP,
  MAX_CROP_SCALE,
  isDefaultCrop,
  normaliseCrop,
  type PhotoCrop,
} from '../api/entry-photos';
import { ApiError } from '../api/client';
import { useSession } from '../auth/SessionProvider';
import { IconClose, IconImage } from '../components/Icons';
import { cropStyle } from '../entries/photo-crop';
import type { EntryPreview } from '../entries/preview';

export interface ComposerPhoto {
  id: string;
  /** Object URL for display. Always local; revoked when the photo goes away. */
  url: string;
  /** Still only in this tab, waiting for the entry to exist. */
  pending: boolean;
  /** The original bytes, kept only while pending so they can be uploaded after the first save. */
  blob?: Blob;
  /** How the writer placed it in its frame, or null for wherever the frame puts it. */
  crop: PhotoCrop | null;
}

export interface EntryPhotoState {
  photos: ComposerPhoto[];
  busy: boolean;
  error: string | null;
  /** Attach files chosen from the picker. */
  add: (files: FileList | File[]) => Promise<void>;
  remove: (id: string) => Promise<void>;
  /** Upload anything held locally, once the entry has an id. Returns nothing; errors surface in `error`. */
  flush: (entryId: string, requireUpload?: boolean) => Promise<void>;
  /** Save how a photo sits in its frame. Applied on screen first, then persisted. */
  setCrop: (id: string, crop: PhotoCrop | null) => Promise<void>;
  /** Re-read the strip from the server. Needed when something outside this hook attaches a photo. */
  reload: () => void;
}

export function useEntryPhotos(entryId: string | null): EntryPhotoState {
  const { vault } = useSession();
  const dek = vault.status === 'unlocked' ? vault.dek : null;

  const [photos, setPhotos] = useState<ComposerPhoto[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Revoking on unmount needs the final list, and the cleanup closure would otherwise capture the
  // list as it was on first render.
  const latest = useRef<ComposerPhoto[]>([]);
  latest.current = photos;
  useEffect(() => () => latest.current.forEach((p) => URL.revokeObjectURL(p.url)), []);

  useEffect(() => {
    if (!entryId || !dek) return;
    const controller = new AbortController();

    (async () => {
      try {
        const list = await fetchEntryPhotos(dek, entryId, controller.signal);
        const loaded = await Promise.all(
          list.map(async (photo) => ({
            id: photo.id,
            url: await loadEntryPhoto(dek, entryId, photo.id, controller.signal),
            pending: false,
            crop: photo.crop,
          })),
        );
        if (controller.signal.aborted) {
          loaded.forEach((p) => URL.revokeObjectURL(p.url));
          return;
        }
        // A reload re-fetches photos this hook already holds, so the URLs it is about to replace have
        // to be revoked or the decrypted bytes stay alive for the life of the tab. Anything still
        // pending is local and unknown to the server, so it survives the swap.
        const previous = latest.current;
        previous.filter((p) => !p.pending).forEach((p) => URL.revokeObjectURL(p.url));
        setPhotos([...loaded, ...previous.filter((p) => p.pending)]);
      } catch {
        // A photo that will not load is not worth blocking the entry over; the writing is the point.
        if (!controller.signal.aborted) setError('Some photos could not be loaded.');
      }
    })();

    return () => controller.abort();
  }, [entryId, dek, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const add = useCallback(
    async (files: FileList | File[]) => {
      const chosen = Array.from(files).filter((f) => f.type.startsWith('image/'));
      if (chosen.length === 0 || !dek) return;

      setBusy(true);
      setError(null);
      try {
        for (const file of chosen) {
          if (entryId) {
            const position = latest.current.length;
            const uploaded = await uploadEntryPhoto(dek, entryId, file, position);
            const url = await loadEntryPhoto(dek, entryId, uploaded.id);
            setPhotos((current) => [...current, { id: uploaded.id, url, pending: false, crop: null }]);
          } else {
            // Shown immediately from the local file, so choosing a photo feels instant even though
            // it cannot be uploaded until the entry is saved.
            setPhotos((current) => [
              ...current,
              {
                id: `pending-${crypto.randomUUID()}`,
                url: URL.createObjectURL(file),
                pending: true,
                blob: file,
                crop: null,
              },
            ]);
          }
        }
      } catch (err) {
        // The Worker's reason is worth passing on — "Photo is too large" tells you what to do next,
        // where a generic failure leaves you re-picking the same file.
        setError(err instanceof ApiError ? err.message : 'That photo could not be added.');
      } finally {
        setBusy(false);
      }
    },
    [dek, entryId],
  );

  const remove = useCallback(
    async (id: string) => {
      const photo = latest.current.find((p) => p.id === id);
      if (!photo) return;

      setPhotos((current) => current.filter((p) => p.id !== id));
      URL.revokeObjectURL(photo.url);
      if (photo.pending || !entryId) return;

      try {
        await deleteEntryPhoto(entryId, id);
      } catch {
        setError('That photo was removed here but may still be on your other devices.');
      }
    },
    [entryId],
  );

  const flush = useCallback(
    async (savedEntryId: string, requireUpload = false) => {
      const waiting = latest.current.filter((p) => p.pending && p.blob);
      if (waiting.length === 0 || !dek) return;

      try {
        let position = latest.current.length - waiting.length;
        for (const photo of waiting) {
          const uploaded = await uploadEntryPhoto(dek, savedEntryId, photo.blob!, position++);
          // A crop chosen before the entry existed is uploaded with it, so adjusting a picture and
          // then saving does not silently discard the adjustment.
          if (photo.crop) {
            await updateEntryPhotoCrop(dek, savedEntryId, uploaded.id, photo.crop).catch(() => {});
          }
          setPhotos((current) =>
            current.map((p) => (p.id === photo.id ? { ...p, id: uploaded.id, pending: false, blob: undefined } : p)),
          );
        }
      } catch {
        setError('Your entry was saved, but some photos could not be uploaded.');
        if (requireUpload) throw new Error('Some photos have not uploaded. Keep this page open and try again.');
      }
    },
    [dek],
  );

  /**
   * Applied on screen straight away and persisted behind it.
   *
   * Optimistic because the crop is already visible in the editor the moment it is confirmed, and a
   * frame that snapped back to centre while a request finished would read as the adjustment having
   * failed. A photo not yet uploaded keeps its crop locally until `flush` sends both together.
   */
  const setCrop = useCallback(
    async (id: string, crop: PhotoCrop | null) => {
      const photo = latest.current.find((p) => p.id === id);
      if (!photo) return;

      const previous = photo.crop;
      setPhotos((current) => current.map((p) => (p.id === id ? { ...p, crop } : p)));
      if (photo.pending || !entryId || !dek) return;

      try {
        await updateEntryPhotoCrop(dek, entryId, id, crop);
      } catch {
        setPhotos((current) => current.map((p) => (p.id === id ? { ...p, crop: previous } : p)));
        setError('That adjustment could not be saved.');
      }
    },
    [dek, entryId],
  );

  return { photos, busy, error, add, remove, flush, reload, setCrop };
}

export function EntryPhotoStrip({
  photos,
  onRemove,
  onOpen,
}: {
  photos: ComposerPhoto[];
  onRemove: (id: string) => void;
  onOpen: (index: number) => void;
}) {
  if (photos.length === 0) return null;

  return (
    <div className="ophotos">
      {photos.map((photo, index) => (
        <div className={`ophoto${photo.pending ? ' pending' : ''}`} key={photo.id}>
          <button className="ophoto-open" onClick={() => onOpen(index)} aria-label={`Photo ${index + 1}`}>
            <img src={photo.url} alt="" loading="lazy" style={cropStyle(photo.crop)} />
          </button>
          <button className="ophoto-x" onClick={() => onRemove(photo.id)} aria-label="Remove photo">
            <IconClose />
          </button>
        </div>
      ))}
    </div>
  );
}

/**
 * The entry's pictures as the facing page's column, at ≥1024px.
 *
 * Unlike the strip, this is never empty. An entry with no photo of its own still gets a cover —
 * `entryPreview`'s answer for it, the same picture the journal's gallery shows — because a column
 * that appears only sometimes is a column that reads as broken the rest of the time.
 *
 * That default has to be unmistakably not a photograph the reader added, or they will wonder where
 * it came from and why they cannot delete it. Two things say so: it carries no remove control, and
 * it is captioned. On a dream the stand-in itself is the way into choosing a different picture;
 * on a waking entry it is not a control, because there is no gallery for it.
 */
export function EntryPhotoColumn({
  photos,
  preview,
  onRemove,
  onOpen,
  onPick,
  busy,
  onChooseCover,
  coverChosen,
}: {
  photos: ComposerPhoto[];
  /** What to show when the entry has none of its own. */
  preview: EntryPreview;
  onRemove: (id: string) => void;
  onOpen: (index: number) => void;
  onPick: (files: FileList) => void;
  busy: boolean;
  /** Dreams only: tap the stand-in to pick a different catalogue picture. */
  onChooseCover?: () => void;
  /** True when the stand-in is the dreamer's pick rather than Oreia's automatic one. */
  coverChosen?: boolean;
}) {
  const [cover, ...rest] = photos;

  return (
    <div className="ophoto-col">
      {cover ? (
        // The × is a sibling of the button that opens the viewer, not a child of it: a button
        // inside a button is not a control anyone can operate.
        <div className={`ophoto-cover${cover.pending ? ' pending' : ''}`}>
          <button className="ophoto-open" onClick={() => onOpen(0)} aria-label="Photo 1">
            <img src={cover.url} alt="" style={cropStyle(cover.crop)} />
          </button>
          <button className="ophoto-x" onClick={() => onRemove(cover.id)} aria-label="Remove photo">
            <IconClose />
          </button>
        </div>
      ) : onChooseCover ? (
        <div className="ophoto-cover">
          <button className="ophoto-choose" onClick={onChooseCover} aria-label="Choose a picture">
            <DefaultCover preview={preview} />
          </button>
        </div>
      ) : (
        <div className="ophoto-cover">
          <DefaultCover preview={preview} />
        </div>
      )}

      {rest.length > 0 ? (
        <div className="ophoto-col-more">
          {rest.map((photo, index) => (
            <div className={`ophoto${photo.pending ? ' pending' : ''}`} key={photo.id}>
              <button
                className="ophoto-open"
                onClick={() => onOpen(index + 1)}
                aria-label={`Photo ${index + 2}`}
              >
                <img src={photo.url} alt="" loading="lazy" style={cropStyle(photo.crop)} />
              </button>
              <button className="ophoto-x" onClick={() => onRemove(photo.id)} aria-label="Remove photo">
                <IconClose />
              </button>
            </div>
          ))}
        </div>
      ) : null}

      {photos.length === 0 ? (
        <p className="ophoto-note">
          {coverChosen
            ? 'The picture you chose for this entry, until you add a photo of your own.'
            : onChooseCover
              ? 'Oreia’s picture for this entry. Tap it to choose another, or add a photo of your own.'
              : 'Oreia’s picture for this entry, until you add one of your own.'}
        </p>
      ) : null}

      <PhotoPickerButton
        onPick={onPick}
        busy={busy}
        className="ophoto-add"
        label={photos.length === 0 ? 'Add a photo' : 'Add another'}
      />
    </div>
  );
}

/** The stand-in cover. Both descriptor shapes, and neither of them removable. */
function DefaultCover({ preview }: { preview: EntryPreview }) {
  if (preview.kind === 'gradient') {
    return <div className="ophoto-art" style={{ background: preview.background }} aria-hidden="true" />;
  }
  return (
    <img
      className="ophoto-art"
      src={preview.url}
      // An empty label means the picture depicts nothing in particular — an aura — and announcing
      // it would only add noise to a column that is already titled.
      alt={preview.label}
      aria-hidden={preview.label ? undefined : true}
      draggable={false}
    />
  );
}

/**
 * The picker. A label rather than a button, because the input it drives has to be real.
 *
 * Two of these are on screen at once on a wide window — one in the action bar, one under the
 * column's cover — and that is fine because they are two views of one control: both hand their
 * files to the same `add` on the same `useEntryPhotos`. A second hook would be two sources of
 * truth for one entry's pictures.
 *
 * The input is offscreen rather than `hidden` so it can still be reached with a Tab key; `hidden`
 * removes it from the focus order and left the only way to attach a photo the mouse.
 */
export function PhotoPickerButton({
  onPick,
  busy,
  className = 'oact',
  label,
}: {
  onPick: (files: FileList) => void;
  busy: boolean;
  /** `oact` is the round toolbar button; the column passes `ophoto-add` for the wide one. */
  className?: string;
  /** Visible text. Without it the control is the icon alone and needs an aria-label instead. */
  label?: string;
}) {
  return (
    <label
      className={className}
      aria-label={label ? undefined : 'Add photos'}
      data-busy={busy || undefined}
    >
      <IconImage />
      {label ? <span>{label}</span> : null}
      <input
        className="ophoto-file"
        type="file"
        accept="image/*"
        multiple
        onChange={(e) => {
          if (e.target.files?.length) onPick(e.target.files);
          // Cleared so choosing the same file twice in a row still fires a change event.
          e.target.value = '';
        }}
      />
    </label>
  );
}

/**
 * Full-size viewer, opened by tapping a thumbnail — and the way into adjusting the photo.
 *
 * Looking and adjusting are one modal rather than two, because they answer the same impulse: you
 * tap a picture because you want to see it properly, and the thing you most often want next is for
 * the frame to be showing a different part of it. Looking stays the default, though — the viewer
 * opens as a viewer, and Adjust is a deliberate step, so tapping a photo never starts an edit
 * nobody asked for.
 */
export function PhotoViewer({
  photos,
  index,
  onClose,
  onCrop,
}: {
  photos: ComposerPhoto[];
  index: number | null;
  onClose: () => void;
  /** Absent on read-only surfaces, which then get the viewer without the Adjust control. */
  onCrop?: (id: string, crop: PhotoCrop | null) => void;
}) {
  const [adjusting, setAdjusting] = useState(false);

  // Closing and reopening on a different photo must not land in the middle of an edit of the one
  // before it.
  useEffect(() => setAdjusting(false), [index]);

  useEffect(() => {
    if (index === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        // Escape backs out one step at a time: an adjustment in progress is abandoned before the
        // viewer is, so it cannot throw away work in a single keystroke.
        if (adjusting) setAdjusting(false);
        else onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [index, onClose, adjusting]);

  if (index === null || !photos[index]) return null;
  const photo = photos[index];

  if (adjusting && onCrop) {
    return (
      <PhotoCropEditor
        photo={photo}
        onCancel={() => setAdjusting(false)}
        // Done closes everything and puts you back at the entry. The viewer shows the whole
        // photograph, not the frame, so staying in it after an adjustment would show the same
        // picture as before and read as if nothing had happened — the change is only visible where
        // it was made for.
        onConfirm={(crop) => {
          onCrop(photo.id, crop);
          setAdjusting(false);
          onClose();
        }}
      />
    );
  }

  return (
    <div className="ophoto-viewer" onClick={onClose} role="dialog" aria-modal="true" aria-label="Photo">
      <img src={photo.url} alt="" />
      <button className="ophoto-viewer-x" onClick={onClose} aria-label="Close">
        <IconClose />
      </button>
      {onCrop ? (
        <div className="ophoto-viewer-bar" onClick={(e) => e.stopPropagation()}>
          <button className="ophoto-btn" onClick={() => setAdjusting(true)}>
            Adjust
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Zoom and reposition a photo inside its frame.
 *
 * The preview is the frame, at the aspect the journal actually uses, and what is inside it is what
 * will be inside it afterwards — so this is direct manipulation rather than a form with numbers in
 * it. Drag moves the picture, the slider zooms, and nothing is written until Done: Cancel leaves
 * the photo exactly as it was found.
 *
 * The stored value is a focal point rather than a rectangle, so the same choice reads correctly in
 * the square strip and the wide cover both. That is why the preview is square: it is the tighter of
 * the two frames, and a subject that survives it survives the other.
 */
function PhotoCropEditor({
  photo,
  onCancel,
  onConfirm,
}: {
  photo: ComposerPhoto;
  onCancel: () => void;
  onConfirm: (crop: PhotoCrop | null) => void;
}) {
  const [crop, setCrop] = useState<PhotoCrop>(() => normaliseCrop(photo.crop ?? DEFAULT_CROP));
  const frame = useRef<HTMLDivElement>(null);
  const drag = useRef<{ pointer: number; x: number; y: number; from: PhotoCrop } | null>(null);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { pointer: e.pointerId, x: e.clientX, y: e.clientY, from: crop };
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const active = drag.current;
    const box = frame.current?.getBoundingClientRect();
    if (!active || active.pointer !== e.pointerId || !box) return;

    // Dragging moves the picture, so the focal point moves the other way — pulling the image right
    // means looking further left. Divided by the frame so the same gesture covers the same
    // proportion of the photo whatever size the window is, and by the zoom so a closed-in photo
    // does not fly past under the same movement.
    setCrop(
      normaliseCrop({
        ...active.from,
        x: active.from.x - ((e.clientX - active.x) / box.width) * (100 / active.from.scale),
        y: active.from.y - ((e.clientY - active.y) / box.height) * (100 / active.from.scale),
      }),
    );
  };

  const endDrag = () => {
    drag.current = null;
  };

  return (
    <div className="ophoto-viewer ophoto-editor" role="dialog" aria-modal="true" aria-label="Adjust photo">
      <div className="ophoto-editor-inner">
        <p className="ophoto-editor-hint">Drag to reposition · pinch or use the slider to zoom</p>

        <div
          className="ophoto-editor-frame"
          ref={frame}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          <img src={photo.url} alt="" draggable={false} style={cropStyle(crop)} />
          {/* Thirds, the same guide every camera app draws, so "slightly left of centre" is a
              judgement the writer can actually make rather than eyeball. */}
          <span className="ophoto-editor-grid" aria-hidden="true" />
        </div>

        <label className="ophoto-editor-zoom">
          <span className="ophoto-editor-zoom-label">Zoom</span>
          <input
            type="range"
            min={1}
            max={MAX_CROP_SCALE}
            step={0.01}
            value={crop.scale}
            aria-label="Zoom"
            onChange={(e) => setCrop((c) => normaliseCrop({ ...c, scale: Number(e.target.value) }))}
          />
        </label>

        <div className="ophoto-editor-actions">
          <button className="ophoto-btn" onClick={onCancel}>
            Cancel
          </button>
          {/* Only offered once there is something to undo, so it is a way out rather than a fourth
              thing to read every time. */}
          <button className="ophoto-btn" onClick={() => setCrop(DEFAULT_CROP)} disabled={isDefaultCrop(crop)}>
            Reset
          </button>
          {/* A crop that is back at the centre is stored as nothing at all, so "reset then done"
              genuinely clears the adjustment instead of saving a value that means the same. */}
          <button
            className="ophoto-btn primary"
            onClick={() => onConfirm(isDefaultCrop(crop) ? null : crop)}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
